export interface PlanLevelGrantTemplate {
  templateId: string;
  planName: string;
  planLevelGrantTemplate: string;
}

export interface ListOfPlansWithoutTemplate {
  templateId: string;
  planName: string;
  planLevelGrantTemplate?: string;
}

export type GrantDateReminderSettings = {
  enabled: boolean;
  recipientRoles: string[];
  pre: {
    enabled: boolean;
    startFromDays?: number;
    intervalDays?: number;
  };
  due: {
    enabled: boolean;
  };
  post: {
    enabled: boolean;
    startFromDays?: number;
    intervalDays?: number;
  };
};

export interface GrantLetterTemplate {
  templateId?: string;
  planName: string;
  templateName?: string;
  esopPlanId?: string;
  s3Path?: string;
  entityType?: string;
  isPrivate: boolean;
  isChanged: boolean;
  isDeleted: boolean;
  isAdded: boolean;
  planLevelGrantTemplate?: string;
  grantLevelTemplate?: string;
  isPlatFormDefault?: boolean;
}

export interface NomineeFormTemplate {
  templateId?: string;
  s3Path?: string;
  entityType?: string;
  fileName?: string;
  fileBlob?: string;
  fileType?: string;
  isChanged: boolean;
  isDeleted: boolean;
  isAdded: boolean;
  isDefault?: boolean;
}

export interface EmployeeHistorySettings {
  internalTransferFieldPaths: string[];
}

export type ReportSettings = {
  maskPiiByDefault: boolean;
  excelPasswordProtectionEnabled: boolean;
  includeGlossaryByDefault: boolean;
  reportHistoryRetentionDays: number;
};
export interface SiteSettingsRes {
  showPPSToEmployees?: boolean;
  emailsToBeSent: boolean;
  approverMailToAdmin: boolean;
  faqForEmployee?: boolean;
  communicationEmailId?: string;
  grantSignature?: string;
  planLevelGrantTemplate: PlanLevelGrantTemplate[];
  listOfPlansWithoutTemplates: ListOfPlansWithoutTemplate[];
  grantLetterTemplates: GrantLetterTemplate[];
  ppsForEmployee?: number;
  acceptanceNumber?: number;
  senderNameForEmail?: string;
  isEsignEnabled: boolean;
  showEmpPortalValuesForecast: boolean;
  overrideGrantLetter: boolean;
  isAttachmentEnabled: boolean;
  isEnabledTwoFactorAuthentication: boolean;
  reminderEmailsToBeSent: boolean;
  reminderEmailsStartFrom?: number;
  reminderEmailsInterval?: number;
  employeeInactivityLogout: boolean;
  shouldEmployeeBeIntimidated: boolean;
  isDocuSignEnabled: boolean;
  isDigioEnabled: boolean;
  signerDetails: SignerDetails[];
  signerDetailsDtos?: SignerDetails[];
  showPlanToEmployees?: boolean;
  sendEmailsToEmployees: boolean;
  sendGrantEmailsToEmployees: boolean;
  sendVestingEmailsToEmployees: boolean;
  sendBuyBackEmailsToEmployees: boolean;
  sendGrantOfferedEmailsToEmployees: boolean;
  generateCredentialsOnGrantOffer: boolean;
  sendGrantWithdrawEmailsToEmployees: boolean;
  sendGrantReminderEmailsToEmployees: boolean;
  grantReminderStartFromEmployee: number;
  grantReminderIntervalEmployee: number;
  sendVestingCompleteEmailsToEmployees: boolean;
  sendBuyBackOfferedEmailsToEmployees: boolean;
  sendBuyBackClosedEmailsToEmployees: boolean;
  sendBuyBackReminderEmailsToEmployees: boolean;
  sendEmailsToAdmin: boolean;
  sendGrantEmailsToAdmin: boolean;
  sendGrantApprovalEmailsToAdmin: boolean;
  grantApprovalEmailActions?: {
    enabled: boolean;
    tokenTtlHours?: number;
  };
  sendGrantApprovalRequestEmailToApprover: boolean;
  sendPlanEmailsToAdmin: boolean;
  sendTransactionEmailsToAdmin: boolean;
  employeeSignatureForGrant: boolean;
  requestApproverSignature: boolean;
  isNomineeMandatory?: boolean;
  showOptionsExpiryDateToEmployees: boolean;
}

export interface SignerDetails {
  id?: number;
  name: string;
  email: string;
  userId?: number;
  signerType?: SignerType;
  toBeAdded?: boolean;
  toBeUpdated?: boolean;
  toBeDeleted?: boolean;
}

export type SignerType = "OPTIONS";

export interface TemplateContent {
  defaultEmailBodyFile?: string;
  emailBodyFile?: string;
  emailSubject?: string;
  emailTemplateName?: string;
  emailTemplateType?: string;
}

export interface CompanyProfileDetails {
  id: string;
  name: string;
  cin: string;
  companyCurrency: string;
  registeredCountry: string;
  dateOfIncorporation: string;
  address: string;
  email: string;
  companyLogoPath: string;
  operatingAs: string;
  nic: string;
  typeOfTheCompany?: string;
  stockSymbol?: string;
  parentCompanyId?: string;
  subsidiaryCompanyIds?: string[];
  companyLogoDisplaySettings?: {
    orientation: "portrait" | "landscape";
    displayFormat: "logoOnly" | "logoAndName" | "nameOnly";
  };
}

export type EmployeePortalSetting = {
  isNomineeMandatory: boolean;
  showPPSToEmployees: boolean;
  showAtGrantPPSToEmployees: boolean;
  showEmpPortalValuesForecast: boolean;
  showPlanToEmployees: boolean;
  mfaMandatoryForEmployee: boolean;
  enableGrantLetterDownload: boolean;
  allowEmployeeStatementDownload: boolean;
  allowEmployeeChatbotAccess: boolean;
  showEquivalentShares: boolean;
  showFractionalPartOfEquivalentShares: boolean;
  showPlanConversionRatio: boolean;
  authMethods: string[];
  generateCredentialsViaLink: boolean;
  allowEmployeesToDownloadPlanDocuments: boolean;
  allowEmployeeToDownloadSettlementLetter: boolean;
  showTransactionsWhenEmpty: boolean;
  hideExerciseValueForEmployees: boolean;
  showRemainingOptionsCardOnDashboard: boolean;
  showOptionsCardOnDashboard: boolean;
  showRealizedCardOnDashboard: boolean;
  showUnrealizedCardOnDashboard: boolean;
  showLossCardOnDashboard: boolean;
  showPercentagesInEmployeePortalTransactions: boolean;
  showVestingTriggerType: boolean;
  showNetGrantValue: boolean;
  showOptionsExpiryDateToEmployees: boolean;
  requireEmployeeNominationSignature: boolean;
  nomineeFormTemplates: NomineeFormTemplate[] | null;
  percentagePrecision?: 2 | 3 | 4;
  grantTagCatalog?: GrantTagCatalogEntry[];
};

export type GrantTagCatalogEntry = {
  label: string;
  value: string;
  active: boolean;
  employeeVisible: boolean;
};
export type EmployeeStatementFilters = {
  employmentStatuses?: string[];
  employmentTypes?: string[];
  excludeEmployeesWOCredentials?: boolean;
  grantStates?: string[];
  planIds?: string[];
};

export type SendEsopStatementToEmployeeConfig = {
  isEnabled: boolean;
  deliverySchedule: {
    frequency: "MONTHLY" | "CUSTOM" | "INTERVAL";
    firstDeliveryMonth: string | null;
    repeatEveryMonths: number | null;
    runOn: "DAY_OF_MONTH" | "LAST_DAY_OF_MONTH" | null;
    dayOfMonth: number | null;
    overflowBehavior: "last_day" | "skip" | "next_month_first" | null;
    customIntervalDays: number | null;
  };
  statementLookbackMonths: number;
  recipientFilters: EmployeeStatementFilters;
};

export type EmailSetting = {
  communicationEmailId: string;
  senderNameForEmail: string;
  reminderEmailsToBeSent: boolean;
  reminderEmailsInterval: number;
  reminderEmailsStartFrom: number;
  isAttachmentEnabled: boolean;
  isAttachmentEnabledForAdmins: boolean;
  employeeInactivityLogout: boolean;
  approverMailToAdmin: boolean;
  emailsToBeSent: boolean;
  sendEmailsToEmployees: boolean;
  sendGrantEmailsToEmployees: boolean;
  sendVestingEmailsToEmployees: boolean;
  sendBuyBackEmailsToEmployees: boolean;
  sendGrantOfferedEmailsToEmployees: boolean;
  generateCredentialsOnGrantOffer: boolean;
  sendGrantWithdrawEmailsToEmployees: boolean;
  sendGrantReminderEmailsToEmployees: boolean;
  grantReminderStartFromEmployee: number;
  grantReminderIntervalEmployee: number;
  sendVestingCompleteEmailsToEmployees: boolean;
  sendBuyBackOfferedEmailsToEmployees: boolean;
  sendBuyBackClosedEmailsToEmployees: boolean;
  sendForfeitureLeterToEmployees: boolean;
  sendBuyBackReminderEmailsToEmployees: boolean;
  sendFullAndFinalLetterToEmployees: boolean;
  sendEmailsToAdmin: boolean;
  sendGrantEmailsToAdmin: boolean;
  sendGrantApprovalEmailsToAdmin: boolean;
  grantApprovalEmailActions?: {
    enabled: boolean;
    tokenTtlHours?: number;
  };
  sendGrantApprovalRequestEmailToApprover: boolean;
  sendPlanEmailsToAdmin: boolean;
  sendTransactionEmailsToAdmin: boolean;
  // employeeSignatureForGrant: boolean;
  sendMonthlyStatement?: boolean;
  monthlyStatementInterval?: number;
  sendEsopStatementToEmployeeConfig: SendEsopStatementToEmployeeConfig;
  emailRecipientRoles?: string[];
  emailTemplateCC?: any;
  sendCCEmailsToAdmins?: boolean;
  sendResignationReminderEmailsToAdmin: boolean;
  sendOptionsExpiryReminderEmailsToEmployee: boolean;
  optionsExpiryReminderStartFromEmployee: number;
  optionsExpiryReminderIntervalEmployee: number;
  sendEmployeeDataSyncRecords: boolean;
  sendGrantAcceptedEmailsToEmployees: boolean;
  sendGrantAcceptedEmailsToAdmin: boolean;
  sendZeroVestingEmailsToEmployees?: boolean;
  sendPendingResignationSummaryEmailsToAdmin: boolean;
  pendingResignationInclusionOffset: number;
  pendingResignationReminderInterval: number;
  sendOptionsExpiryReminderEmailsToAdmin: boolean;
  optionsExpiryReminderStartFromAdmin: number;
  optionsExpiryReminderIntervalAdmin: number;
  sendMilestoneCompletionReminderEmailsToAdmin: boolean;
  sendMilestoneReminderEmailsToAdmin: boolean;
  milestoneReminderStartFromAdmin: number;
  milestoneReminderIntervalAdmin: number;
  grantDateReminder: GrantDateReminderSettings;
  sendEmailsToManagers: boolean;
  sendManagerPortalAccessEmailToManagers: boolean;
  sendManagerEmployeeAssignedEmailToManagers: boolean;
  sendManagerEmployeeRemovedEmailToManagers: boolean;
};

export type GrantLetterSetting = {
  acceptanceNumber: number;
  employeeSignatureForGrant: boolean;
  requestApproverSignature: boolean;
  overrideGrantLetter: boolean;
  enableAutoExerciseRequest: boolean;
  grantLetterTemplates: GrantLetterTemplate[];
  authorizedSignature: string | undefined;
  dateFormat?: string;
  dateForLapsing?: string;
  autoPauseVestingOnResignation?: boolean;
  discardOfferedGrantsOnResignation?: boolean;
  generatePlanDocument?: boolean;
  showZeroVestingInGrantLetter?: boolean;
  showValueAtGrantToAdmin?: boolean;
  excludeWithdrawnFromTotal?: boolean;
  showActiveGrantedOptionsToAdmin?: boolean;
  disableAutoGrantLetterGeneration?: boolean;
  useAllocationMatrixForGranting?: boolean;
  defaultAllocationMatrixId?: string | null;
  showGrantAcceptanceDisclaimer?: boolean;
  grantAcceptanceDisclaimerText?: string;
};

export type ESignSetting = {
  isEsignEnabled: boolean;
  isDocuSignEnabled: boolean;
  signerDetails: SignerDetails[];
  signerDetailsDtos?: SignerDetails[];
  isDigioEnabled: boolean;
  useDigioElectronic?: boolean;
  useDigioAadhar?: boolean;
};

export type ListOfEmailTemplates = {
  companyId?: string;
  id: string;
  templateName: string;
  templateType: string;
  name: string;
  description: string;
  recipient?: string;
  subject: string;
  overriddenByCompany: boolean;
  content?: string;
  isEdited?: boolean;
};

export interface EmailTemplate {
  content?: string;
  footer?: string;
  header?: string;
  subject?: string;
}

export interface EmailTemplateSaveObject {
  emailTemplateBody?: string;
  templateId: string;
  isEdited?: boolean;
  subject?: string;
  emailTemplateBodyBase64?: string;
}
export type EmailCCSettings = {
  sendCCEmailsToAdmins: boolean;
  emailTemplateCC?: { [k: string]: string[] };
};

export const AuthMethods: { [key: string]: string } = {
  "Password Based": "passwordBased",
  "Sign in with Google": "google",
  "Sign in with Microsoft": "microsoft",
};
